import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { summaryAPI, shiftsAPI, exportAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, FileDown, Users, Settings, LogOut, ShieldCheck, CalendarDays, Copy, Plus, Building2, Ticket, Layers, BarChart3 } from 'lucide-react';
import { format, startOfWeek, addWeeks, subWeeks, addDays, getISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import ShiftEditDialog from '@/components/ShiftEditDialog';
import OvertimeDialog from '@/components/OvertimeDialog';
import EmployeeManageDialog from '@/components/EmployeeManageDialog';
import TwoFADialog from '@/components/TwoFADialog';
import CopyWeekDialog from '@/components/CopyWeekDialog';
import EmployeeRecapDialog from '@/components/EmployeeRecapDialog';
import CompanyManageDialog from '@/components/CompanyManageDialog';
import InvitationCodesDialog from '@/components/InvitationCodesDialog';
import WeekTemplatesDialog from '@/components/WeekTemplatesDialog';
import AdminDashboardDialog from '@/components/AdminDashboardDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const DAY_NAMES = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];

const SHIFT_COLORS = {
  normal: { bg: 'bg-blue-50', text: 'text-blue-900', border: 'border-blue-200' },
  ecole: { bg: 'bg-purple-50', text: 'text-purple-900', border: 'border-purple-200' },
  conge: { bg: 'bg-amber-50', text: 'text-amber-900', border: 'border-amber-200' },
  maladie: { bg: 'bg-red-50', text: 'text-red-900', border: 'border-red-200' },
};

const SHIFT_LABELS = {
  normal: '',
  ecole: 'École',
  conge: 'Congé',
  maladie: 'Maladie',
};

function formatHoursDiff(hours) {
  const abs = Math.abs(hours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  const sign = hours >= 0 ? '+' : '-';
  return `${sign}${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
}

function formatHours(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    return startOfWeek(now, { weekStartsOn: 1 });
  });
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftDialog, setShiftDialog] = useState({ open: false, employeeId: null, date: null, shift: null });
  const [overtimeDialog, setOvertimeDialog] = useState({ open: false, employee: null });
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [twoFADialog, setTwoFADialog] = useState(false);
  const [copyWeekDialog, setCopyWeekDialog] = useState(false);
  const [recapDialog, setRecapDialog] = useState({ open: false, employee: null });
  const [companyDialog, setCompanyDialog] = useState(false);
  const [invitationDialog, setInvitationDialog] = useState(false);
  const [templatesDialog, setTemplatesDialog] = useState(false);
  const [adminDashDialog, setAdminDashDialog] = useState(false);

  const isAdmin = user?.is_admin;

  const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd');
  const weekNumber = getISOWeek(currentWeekStart);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await summaryAPI.weekly(weekStartStr);
      setSummaries(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des données');
    }
    setLoading(false);
  }, [weekStartStr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const handleExportPDF = async () => {
    try {
      const month = format(currentWeekStart, 'yyyy-MM');
      const res = await exportAPI.monthlyPDF(month);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `recap_${month}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exporté');
    } catch {
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const getShiftsForDay = (shifts, dateStr) => {
    return shifts?.filter(s => s.date === dateStr) || [];
  };

  const handleCellClick = (employeeId, date) => {
    if (!isAdmin) return;
    setShiftDialog({ open: true, employeeId, date: format(date, 'yyyy-MM-dd'), shift: null });
  };

  const handleShiftClick = (e, employeeId, date, shift) => {
    e.stopPropagation();
    if (!isAdmin) return;
    setShiftDialog({ open: true, employeeId, date: format(date, 'yyyy-MM-dd'), shift });
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      await shiftsAPI.delete(shiftId);
      toast.success('Shift supprimé');
      fetchData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  return (
    <TooltipProvider>
      <div data-testid="dashboard" className="min-h-screen bg-slate-50">
        {/* Header */}
        <header data-testid="dashboard-header" className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
                <CalendarDays className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-bold text-slate-900 text-lg hidden sm:inline">Planning Équipe</span>
            </div>

            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  data-testid="admin-dashboard-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setAdminDashDialog(true)}
                  className="gap-2"
                >
                  <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Vue globale</span>
                </Button>
              )}

              {isAdmin && (
                <Button
                  data-testid="templates-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setTemplatesDialog(true)}
                  className="gap-2"
                >
                  <Layers className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Templates</span>
                </Button>
              )}

              {isAdmin && (
                <Button
                  data-testid="copy-week-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setCopyWeekDialog(true)}
                  className="gap-2"
                >
                  <Copy className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Copier semaine</span>
                </Button>
              )}

              {isAdmin && (
                <Button
                  data-testid="export-pdf-btn"
                  variant="outline"
                  size="sm"
                  onClick={handleExportPDF}
                  className="gap-2"
                >
                  <FileDown className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Export PDF</span>
                </Button>
              )}

              {isAdmin && (
                <Button
                  data-testid="manage-companies-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setCompanyDialog(true)}
                  className="gap-2"
                >
                  <Building2 className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Sociétés</span>
                </Button>
              )}

              {isAdmin && (
                <Button
                  data-testid="manage-codes-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setInvitationDialog(true)}
                  className="gap-2"
                >
                  <Ticket className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Codes</span>
                </Button>
              )}

              {isAdmin && (
                <Button
                  data-testid="manage-employees-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setEmployeeDialog(true)}
                  className="gap-2"
                >
                  <Users className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Employés</span>
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="user-menu-btn" variant="ghost" size="sm" className="gap-2">
                    <Settings className="w-4 h-4" strokeWidth={1.5} />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem data-testid="2fa-menu-item" onClick={() => setTwoFADialog(true)}>
                    <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {user?.two_fa_enabled ? 'Gérer 2FA' : 'Activer 2FA'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="logout-menu-item" onClick={logout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Week Navigation */}
        <div data-testid="week-navigator" className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Button
            data-testid="prev-week-btn"
            variant="outline"
            size="icon"
            onClick={() => setCurrentWeekStart(prev => subWeeks(prev, 1))}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </Button>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-1.5 rounded-full text-sm font-semibold">
              S. {weekNumber}
            </div>
            <p className="text-sm text-slate-500 mt-1 font-data">
              {format(currentWeekStart, 'd MMM', { locale: fr })} - {format(addDays(currentWeekStart, 6), 'd MMM yyyy', { locale: fr })}
            </p>
          </div>

          <Button
            data-testid="next-week-btn"
            variant="outline"
            size="icon"
            onClick={() => setCurrentWeekStart(prev => addWeeks(prev, 1))}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>

        {/* Planning Grid */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-8">
          <div data-testid="planning-grid" className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            {/* Grid Header */}
            <div className="grid grid-cols-[220px_repeat(7,1fr)] min-w-[1000px]">
              <div className="p-3 border-b border-r border-slate-200 bg-slate-50">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Employé</span>
              </div>
              {days.map((day, i) => (
                <div
                  key={i}
                  className={`p-3 border-b border-slate-200 text-center ${i < 6 ? 'border-r' : ''} ${isToday(day) ? 'bg-slate-900' : 'bg-slate-50'}`}
                >
                  <span className={`text-xs font-bold tracking-wider ${isToday(day) ? 'text-white' : 'text-slate-500'}`}>
                    {DAY_NAMES[i]}. {format(day, 'dd')}
                  </span>
                </div>
              ))}
            </div>

            {/* Employee Rows */}
            {loading ? (
              <div className="p-12 text-center text-slate-400">Chargement...</div>
            ) : summaries.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                <p>Aucun employé. Ajoutez-en un pour commencer.</p>
              </div>
            ) : (
              summaries.map((summary, idx) => {
                const { employee, shifts, total_hours, contract_hours, week_diff, overtime_balance } = summary;
                return (
                  <div
                    key={employee.id}
                    data-testid={`employee-row-${employee.id}`}
                    className={`grid grid-cols-[220px_repeat(7,1fr)] min-w-[1000px] ${idx < summaries.length - 1 ? 'border-b border-slate-100' : ''} hover:bg-slate-50/50 transition-colors`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    {/* Employee Info */}
                    <div className="p-3 border-r border-slate-200 flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400 font-data">{employee.initials}</span>
                        <button
                          data-testid={`employee-name-${employee.id}`}
                          onClick={() => {
                            // Admin can see any recap, employee can see own recap only
                            if (isAdmin || user?.employee_id === employee.id) {
                              setRecapDialog({ open: true, employee });
                            }
                          }}
                          className={`text-sm font-semibold text-left transition-colors ${(isAdmin || user?.employee_id === employee.id) ? 'text-slate-900 hover:text-blue-700 hover:underline cursor-pointer' : 'text-slate-900 cursor-default'}`}
                        >
                          {employee.first_name} {employee.last_name.toUpperCase()}
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-slate-500 font-data">{employee.contract_hours}h</span>
                        <span className="text-xs text-slate-300">|</span>
                        <span className="text-xs text-slate-500 font-data">{formatHours(total_hours)}</span>
                        {isAdmin && (
                          <>
                            <span className="text-xs text-slate-300">|</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`week-diff-${employee.id}`}
                                  className={`text-xs font-semibold font-data px-1.5 py-0.5 rounded cursor-pointer ${week_diff >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}
                                >
                                  {formatHoursDiff(week_diff)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Différence semaine</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  data-testid={`overtime-btn-${employee.id}`}
                                  onClick={() => setOvertimeDialog({ open: true, employee })}
                                  className={`text-xs font-semibold font-data px-1.5 py-0.5 rounded cursor-pointer transition-all hover:brightness-95 ${overtime_balance >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}
                                >
                                  H.S {formatHoursDiff(overtime_balance)}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Cliquer pour ajuster les heures supp.</TooltipContent>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Day Cells */}
                    {days.map((day, dayIdx) => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const dayShifts = getShiftsForDay(shifts, dateStr);

                      return (
                        <div
                          key={dayIdx}
                          data-testid={`cell-${employee.id}-${dateStr}`}
                          className={`p-1.5 ${dayIdx < 6 ? 'border-r border-slate-100' : ''} ${isAdmin ? 'cursor-pointer hover:bg-slate-50' : ''} transition-colors min-h-[72px] flex flex-col gap-1 justify-center`}
                          onClick={() => handleCellClick(employee.id, day)}
                        >
                          {dayShifts.map((shift) => {
                            const colors = SHIFT_COLORS[shift.shift_type] || SHIFT_COLORS.normal;
                            return (
                              <div
                                key={shift.id}
                                data-testid={`shift-block-${shift.id}`}
                                className={`w-full rounded-lg p-1.5 text-center border ${colors.bg} ${colors.text} ${colors.border} shadow-sm transition-all ${isAdmin ? 'hover:brightness-90 cursor-pointer' : ''}`}
                                onClick={(e) => handleShiftClick(e, employee.id, day, shift)}
                              >
                                {shift.shift_type !== 'normal' ? (
                                  <div className="text-xs font-semibold">{SHIFT_LABELS[shift.shift_type]}</div>
                                ) : null}
                                {shift.start_time !== '00:00' && (
                                  <div className="text-xs font-data font-medium">
                                    {shift.start_time} - {shift.end_time}
                                  </div>
                                )}
                                {shift.break_minutes > 0 && shift.shift_type === 'normal' && (
                                  <div className="text-[10px] opacity-70 font-data">({shift.break_minutes}mn)</div>
                                )}
                              </div>
                            );
                          })}
                          {/* Add shift button when shifts already exist - admin only */}
                          {isAdmin && dayShifts.length > 0 && (
                            <button
                              data-testid={`add-shift-${employee.id}-${dateStr}`}
                              className="w-full flex items-center justify-center py-0.5 rounded border border-dashed border-slate-200 text-slate-300 hover:text-slate-500 hover:border-slate-400 transition-colors"
                              onClick={(e) => { e.stopPropagation(); handleCellClick(employee.id, day); }}
                            >
                              <Plus className="w-3 h-3" strokeWidth={2} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Dialogs */}
        <ShiftEditDialog
          open={shiftDialog.open}
          onClose={() => setShiftDialog({ open: false, employeeId: null, date: null, shift: null })}
          employeeId={shiftDialog.employeeId}
          date={shiftDialog.date}
          shift={shiftDialog.shift}
          onSave={fetchData}
          onDelete={handleDeleteShift}
        />

        <OvertimeDialog
          open={overtimeDialog.open}
          onClose={() => setOvertimeDialog({ open: false, employee: null })}
          employee={overtimeDialog.employee}
          onSave={fetchData}
        />

        <EmployeeManageDialog
          open={employeeDialog}
          onClose={() => setEmployeeDialog(false)}
          onSave={fetchData}
        />

        <TwoFADialog
          open={twoFADialog}
          onClose={() => setTwoFADialog(false)}
        />

        <CopyWeekDialog
          open={copyWeekDialog}
          onClose={() => setCopyWeekDialog(false)}
          sourceWeekStart={currentWeekStart}
          onCopied={(targetWeek) => {
            setCurrentWeekStart(targetWeek);
          }}
        />

        <EmployeeRecapDialog
          open={recapDialog.open}
          onClose={() => setRecapDialog({ open: false, employee: null })}
          employee={recapDialog.employee}
          isAdmin={isAdmin}
        />

        <CompanyManageDialog
          open={companyDialog}
          onClose={() => setCompanyDialog(false)}
          onSave={fetchData}
        />

        <InvitationCodesDialog
          open={invitationDialog}
          onClose={() => setInvitationDialog(false)}
        />

        <WeekTemplatesDialog
          open={templatesDialog}
          onClose={() => setTemplatesDialog(false)}
          currentWeekStart={currentWeekStart}
          onApplied={(targetWeek) => {
            setCurrentWeekStart(targetWeek);
            setTemplatesDialog(false);
          }}
        />

        <AdminDashboardDialog
          open={adminDashDialog}
          onClose={() => setAdminDashDialog(false)}
        />
      </div>
    </TooltipProvider>
  );
}
